
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, signal, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { NgbCalendar, NgbDate, NgbDatepickerModule, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-datepicker',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, NgbDatepickerModule],
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DatepickerComponent,
      multi: true
    }
  ]
})
export class DatepickerComponent implements ControlValueAccessor, OnInit {
  @Input() label?: string;
  @Input() required: boolean = false;
  @Input() minDate?: NgbDateStruct;
  @Input() maxDate?: NgbDateStruct;
  @Input() showValidation: boolean = false;
  @Input() validationMessage?: string;
  @Input() range: boolean = false;
  @Input() displayMonths: number = 1;
  @Input() hideNavigation: boolean = false;
  @Input() startMonth?: NgbDateStruct; // Optional: specify which month to start with (defaults to current month)
  @Output() dateChange = new EventEmitter<string | null>();
  @Output() dateRangeChange = new EventEmitter<{ fromDate: string | null; toDate: string | null }>();

  @ViewChild('customDayTemplate', { static: true }) customDayTemplate!: TemplateRef<any>;

  // Single date mode
  model = signal<NgbDateStruct | null>(null);

  // Range mode
  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate | null = null;
  toDate: NgbDate | null = null;
  startDate = signal<NgbDateStruct | null>(null); // For showing previous month on left

  disabled = signal<boolean>(false);

  private onChange = (value: string | null | { fromDate: string | null; toDate: string | null } | null) => {};
  onTouched = () => {};

  constructor(
    private cdr: ChangeDetectorRef,
    private calendar: NgbCalendar
  ) {}

  ngOnInit() {
    // Set startDate - use provided startMonth, or calculate based on displayMonths
    if (this.startMonth) {
      this.startDate.set(this.startMonth);
    } else if (this.displayMonths > 1) {
      // Default behavior: start one month before current month
      const today = this.calendar.getToday();
      const firstDayOfCurrentMonth = new NgbDate(today.year, today.month, 1);
      const firstDayOfPreviousMonth = this.calendar.getPrev(firstDayOfCurrentMonth, 'm', 1);
      this.startDate.set({ year: firstDayOfPreviousMonth.year, month: firstDayOfPreviousMonth.month, day: 1 });
    }
  }

  onDateSelect(date: NgbDate | NgbDateStruct | null) {
    if (this.range) {
      this.onRangeDateSelect(date as NgbDate);
    } else {
      this.onSingleDateSelect(date as NgbDateStruct | null);
    }
  }

  private onSingleDateSelect(date: NgbDateStruct | null) {
    if (date) {
      this.model.set(date);
      const formatted = this.formatDate(date);
      this.onChange(formatted);
      this.dateChange.emit(formatted);
    } else {
      this.model.set(null);
      this.onChange(null);
      this.dateChange.emit(null);
    }
    this.onTouched();
  }

  private onRangeDateSelect(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && (date.after(this.fromDate) || date.equals(this.fromDate))) {
      // Allow selecting the same date for start and end (single day range)
      this.toDate = date;
      this.hoveredDate = null;
      this.emitRangeValue();
    } else {
      this.toDate = null;
      this.fromDate = date;
      this.hoveredDate = null;
    }
    this.cdr.markForCheck();
    this.onTouched();
  }

  isRange(date: NgbDate): boolean {
    return (this.fromDate !== null && this.fromDate.equals(date)) ||
           (this.toDate !== null && this.toDate.equals(date));
  }

  isInsideButNotRange(date: NgbDate): boolean {
    // Return true if date is inside the range but NOT the start or end date
    if (this.toDate === null || this.fromDate === null) {
      return false;
    }
    return date.after(this.fromDate) && date.before(this.toDate);
  }

  isHovered(date: NgbDate): boolean {
    return this.fromDate !== null && !this.toDate && this.hoveredDate !== null &&
           (date.after(this.fromDate) || date.equals(this.fromDate)) &&
           (date.before(this.hoveredDate) || date.equals(this.hoveredDate));
  }

  isDisabled(date: NgbDate): boolean {
    // Check if date is outside minDate/maxDate range
    if (this.minDate) {
      const min = new NgbDate(this.minDate.year, this.minDate.month, this.minDate.day);
      if (date.before(min)) {
        return true;
      }
    }
    if (this.maxDate) {
      const max = new NgbDate(this.maxDate.year, this.maxDate.month, this.maxDate.day);
      if (date.after(max)) {
        return true;
      }
    }
    return false;
  }

  private emitRangeValue() {
    const value = {
      fromDate: this.fromDate ? this.formatDate(this.fromDate) : null,
      toDate: this.toDate ? this.formatDate(this.toDate) : null
    };
    this.onChange(value);
    this.dateRangeChange.emit(value);
  }

  // ControlValueAccessor implementation
  writeValue(value: string | null | { fromDate: string | null; toDate: string | null } | null): void {
    if (this.range) {
      if (value && typeof value === 'object' && 'fromDate' in value && value.fromDate && value.toDate) {
        const from = this.parseDateString(value.fromDate);
        const to = this.parseDateString(value.toDate);
        if (from && to) {
          // Only update if values actually changed to avoid unnecessary change detection
          const fromChanged = !this.fromDate || !this.fromDate.equals(from);
          const toChanged = !this.toDate || !this.toDate.equals(to);

          if (fromChanged || toChanged) {
            this.fromDate = from;
            this.toDate = to;
            this.hoveredDate = null;
            this.cdr.markForCheck();
          }
        }
      } else {
        // Only update if we had values before
        if (this.fromDate || this.toDate) {
          this.fromDate = null;
          this.toDate = null;
          this.hoveredDate = null;
          this.cdr.markForCheck();
        }
      }
    } else {
      if (value && typeof value === 'string') {
        this.model.set(this.parseDate(value));
      } else {
        this.model.set(null);
      }
    }
  }

  registerOnChange(fn: (value: string | null | { fromDate: string | null; toDate: string | null } | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Helper methods
  /**
   * Convert a JavaScript Date object to NgbDateStruct
   * Note: NgbDate uses 1-based months (1-12), while JavaScript Date uses 0-based months (0-11)
   */
  static dateToNgbDateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1, // Convert from 0-based to 1-based
      day: date.getDate()
    };
  }

  private formatDate(date: NgbDateStruct | NgbDate): string {
    return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
  }

  private parseDate(dateString: string): NgbDateStruct | null {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    return { year, month, day };
  }

  private parseDateString(dateString: string): NgbDate | null {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    return new NgbDate(year, month, day);
  }
}
